import { createRouter, createWebHistory } from 'vue-router'

import { isAuthenticated } from '@/libs/auth'
import { useAppStore } from '@/stores/app'

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) return savedPosition
        else if (to.hash) return { selector: to.hash }
        else if (from.path === to.path) return {}

        return {
            top: 1,
            behavior: 'smooth'
        }
    },
    routes: [
        {
            path: '/',
            name: 'dashboard',
            component: () => import('@/views/Dashboard.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/users',
            name: 'users',
            component: () => import('@/views/user/Users.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/vat-groups',
            name: 'vat-groups',
            component: () => import('@/views/vatGroup/VatGroups.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/discount-coupons',
            name: 'discount-coupons',
            component: () => import('@/views/discountCoupon/DiscountCoupons.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/categories',
            name: 'categories',
            component: () => import('@/views/category/Categories.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/sale-websites',
            name: 'sale-websites',
            component: () => import('@/views/saleWebsite/SaleWebsites.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/sale-website/:id',
            name: 'sale-website',
            component: () => import('@/views/saleWebsite/SaleWebsite.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/sale-website/:sale_website_id/additional-page/add',
            name: 'sale-website-additional-page-add',
            component: () => import('@/views/saleWebsite/additionalPage/AdditionalPage.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/sale-website/:sale_website_id/additional-page/:id',
            name: 'sale-website-additional-page',
            component: () => import('@/views/saleWebsite/additionalPage/AdditionalPage.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/product-brands',
            name: 'product-brands',
            component: () => import('@/views/productBrand/ProductBrands.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/product-brand/:id',
            name: 'product-brand',
            component: () => import('@/views/productBrand/ProductBrand.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/products',
            name: 'products',
            component: () => import('@/views/product/Products.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/product/:synced_id',
            name: 'product',
            component: () => import('@/views/product/Product.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/add-product',
            name: 'add-product',
            component: () => import('@/views/product/ModifyProduct.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/edit-product/:synced_id',
            name: 'edit-product',
            component: () => import('@/views/product/ModifyProduct.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/product-specifications',
            name: 'product-specifications',
            component: () => import('@/views/productSpecification/ProductSpecifications.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/required-category-specifications',
            name: 'required-category-specifications',
            component: () => import('@/views/requiredCategorySpecification/RequiredCategorySpecifications.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/specification-templates',
            name: 'specification-templates',
            component: () => import('@/views/specificationTemplate/SpecificationTemplates.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/specification-template/add',
            name: 'specification-template-add',
            component: () => import('@/views/specificationTemplate/SpecificationTemplate.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/specification-template/:id',
            name: 'specification-template',
            component: () => import('@/views/specificationTemplate/SpecificationTemplate.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/country-configurations',
            name: 'country-configurations',
            component: () => import('@/views/configuration/CountryConfigurations.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/sale-partners',
            name: 'sale-partners',
            component: () => import('@/views/salePartner/SalePartners.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/sale-partner/:id/prices',
            name: 'sale-partner-prices',
            component: () => import('@/views/salePartner/SalePartnerPrices.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/orders',
            name: 'orders',
            component: () => import('@/views/order/Orders.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/order/:id',
            name: 'order',
            component: () => import('@/views/order/Order.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/settings',
            name: 'settings',
            component: () => import('@/views/Settings.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/instructions',
            name: 'instructions',
            component: () => import('@/views/Instructions.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/profile',
            name: 'profile',
            component: () => import('@/views/profile/Profile.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/login',
            name: 'login',
            component: () => import('@/views/Login.vue'),
            meta: {
                layout: 'Blank',
                requiresAuth: false
            }
        },
        {
            path: '/:pathMatch(.*)*',
            redirect: '/'
        }
    ]
})

router.beforeEach((to, from, next) => {
    const loggedIn: boolean = isAuthenticated()
    const appStore = useAppStore()

    if (loggedIn && to.name === 'login') {
        next({ name: 'dashboard' })
        return
    }

    if (to.meta.requiresAuth && !isAuthenticated()) {
        appStore.setLogout()
        next({ name: 'login' })
    }

    next()
})

export default router
